import React, { PropsWithChildren, createContext, useContext, useEffect } from "react";

import useRefOf from "../../../hooks/useRefOf";

type Listener = () => void;

const closableContext = createContext<boolean>(true);

/** Returns false if this element is in an inactive tab anywhere in its hierarchy, and true otherwise. */
export function useParentIsOpen() {
  return useContext(closableContext);
}

/** Runs some code when the element closes. */
export default function useOnParentClose(onClose?: Listener | null) {
  const parentIsOpen = useParentIsOpen();
  const onCloseRef = useRefOf(onClose);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (parentIsOpen) return () => void onCloseRef.current?.();
  }, [onCloseRef, parentIsOpen]);
}

export function Closable({
  children,
  isOpen,
  isLeaf,
}: PropsWithChildren<{
  /** Whether or not this element is open */
  isOpen: boolean;
  /** Whether or not this element is allowed to have children — if it is not then we assume any modals created from inside it are parented to our parent instead of to us. What that means in practice is that nobody cares if THIS element closes and therefore it's more helpful to pass through the parent's open/closed status */
  isLeaf?: boolean;
}>) {
  const parentIsOpen = useContext(closableContext);
  if (isLeaf) return <>{children}</>;
  return <closableContext.Provider value={parentIsOpen && isOpen}>{children}</closableContext.Provider>;
}
